import { alpha, Theme } from '@mui/material/styles';
import { ButtonProps } from '@mui/material';

// ----------------------------------------------------------------------

const COLORS = [
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
] as const;

// NEW VARIANT
declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        soft: true;
    }
}

export default function Button(theme: Theme) {
    const isLight = theme.palette.mode === 'light';

    const rootStyle = (ownerState: ButtonProps) => {
        const inheritColor = ownerState.color === 'inherit';

        const containedVariant = ownerState.variant === 'contained';
        const outlinedVariant = ownerState.variant === 'outlined';
        const textVariant = ownerState.variant === 'text';
        const softVariant = ownerState.variant === 'soft';

        const smallSize = ownerState.size === 'small';
        const largeSize = ownerState.size === 'large';

        const defaultStyle = {
            ...(inheritColor && {
                // CONTAINED
                ...(containedVariant && {
                    color: theme.palette.grey[800],
                    '&:hover': {
                        boxShadow: theme.customShadows.z8,
                        backgroundColor: theme.palette.grey[400],
                    },
                    '&:focus': {
                        // Remove focus border
                        outline: 'none',
                        boxShadow: 'none',
                    },
                }),
                // OUTLINED
                ...(outlinedVariant && {
                    borderColor: alpha(theme.palette.grey[500], 0.32),
                    '&:hover': {
                        borderColor: theme.palette.text.primary,
                        backgroundColor: theme.palette.action.hover,
                    },
                    '&:focus': {
                        // Remove focus border
                        outline: 'none',
                        boxShadow: 'none',
                    },
                }),
                // TEXT
                ...(textVariant && {
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                    '&:focus': {
                        // Remove focus border
                        outline: 'none',
                        boxShadow: 'none',
                    },
                }),
                // SOFT
                ...(softVariant && {
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.grey[500], 0.08),
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.grey[500], 0.24),
                    },
                    '&:focus': {
                        // Remove focus border
                        outline: 'none',
                        boxShadow: 'none',
                    },
                }),
            }),
        };

        const colorStyle = COLORS.map((color) => ({
            ...(ownerState.color === color && {
                // CONTAINED
                ...(containedVariant && {
                    '&:hover': {
                        boxShadow: theme.customShadows[color],
                    },
                    '&:focus': {
                        // Remove focus border
                        outline: 'none',
                        boxShadow: 'none',
                    },
                }),
                // SOFT
                ...(softVariant && {
                    color: theme.palette[color][isLight ? 'dark' : 'light'],
                    backgroundColor: alpha(theme.palette[color].main, 0.16),
                    '&:hover': {
                        backgroundColor: alpha(theme.palette[color].main, 0.32),
                    },
                    '&:focus': {
                        // Remove focus border
                        outline: 'none',
                        boxShadow: 'none',
                    },
                }),
            }),
        }));

        const disabledState = {
            '&.Mui-disabled': {
                // SOFT
                ...(softVariant && {
                    backgroundColor: theme.palette.action.disabledBackground,
                }),
            },
        };

        const size = {
            ...(smallSize && {
                height: theme.componentSizes.small,
                fontSize: 13,
                padding: theme.spacing(1.5, 3),
            }),
            ...(largeSize && {
                height: theme.componentSizes.large,
                fontSize: 15,
                padding: theme.spacing(2, 4),
            }),
        };

        return [...colorStyle, defaultStyle, disabledState, size];
    };

    return {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },

            styleOverrides: {
                root: ({ ownerState }: { ownerState: ButtonProps }) =>
                    rootStyle(ownerState),
                startIcon: {
                    marginInlineStart: '-4px',
                    marginInlineEnd: '8px',
                },
                endIcon: {
                    marginInlineStart: '8px',
                    marginInlineEnd: '-4px',
                },
            },
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    '&.MuiIconButton-root.MuiAutocomplete-popupIndicator, &.MuiIconButton-root.MuiAutocomplete-clearIndicator':
                        {
                            height: '32px',
                            width: '32px',
                        },
                    '&.MuiChip-root .MuiChip-deleteIcon': {
                        marginInlineEnd: '5px',
                        marginRight: '0',
                    },
                    '&.MuiListItemButton-root': {
                        textAlign: 'start',
                    },
                },
            },
        },
    };
}
